import React, {
	useState,
	useEffect,
	useRef
} from 'react';
import {
	Search,
	Download,
	ExternalLink,
	Loader,
	Filter,
	X,
	Clock,
	Copy
} from 'lucide-react';

const setCookie = (name, value, days) => {
	const date = new Date();
	date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
	const expires = `expires=${date.toUTCString()}`;
	document.cookie = `${name}=${value};${expires};path=/`;
};

const getCookie = (name) => {
	const nameEQ = name + "=";
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
	}
	return null;
};

const WORKER_URL = 'https://gameapi.a7a8524.workers.dev'; // Replace with your actual worker URL

const GameSearchApp = () => {
	const [query,
		setQuery] = useState('');
	const [refineQuery,
		setRefineQuery] = useState('');
	const [results,
		setResults] = useState([]);
	const [recentUploads,
		setRecentUploads] = useState([]);
	const [loading,
		setLoading] = useState(false);
	const [loadingRecent,
		setLoadingRecent] = useState(false);
	const [error,
		setError] = useState('');
	const [siteFilter,
		setSiteFilter] = useState('all');
	const [stats,
		setStats] = useState( {});
	const [searchHistory,
		setSearchHistory] = useState([]);
	const [hasSearched,
		setHasSearched] = useState(false);
	const [failedImages,
		setFailedImages] = useState(new Set());
	const [decryptedLinks,
		setDecryptedLinks] = useState( {}); // store decrypted crypt links
	const [sortOrder,
		setSortOrder] = useState('relevance');

	const searchInputRef = useRef(null);

	const [hideAdblockNotice,
		setHideAdblockNotice] = useState(() => {
			// Check if the cookie exists and return true if it does, false otherwise
			return getCookie('hideAdblockNotice') === 'true';
		});

	// Add this useEffect to set the cookie when the notice is closed
	useEffect(() => {
		if (hideAdblockNotice) {
			// Set cookie to expire in 30 days
			setCookie('adblockNoticeClosed', 'true', 30);
		}
	},
		[hideAdblockNotice]);

	// Url state management
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);

		const q = params.get('q') || '';
		const site = params.get('site') || 'all';
		const sort = params.get('sort') || 'relevance';

		setQuery(q);
		setSiteFilter(site);
		setSortOrder(sort);

		if (q) {
			searchGames(q);
		}
	},
		[]); // run once

	useEffect(() => {
		const params = new URLSearchParams();

		if (query) params.set('q', query);
		if (siteFilter !== 'all') params.set('site', siteFilter);
		if (sortOrder !== 'relevance') params.set('sort', sortOrder);

		const newUrl = `${window.location.pathname}?${params.toString()}`;
		window.history.replaceState({}, '', newUrl);
	},
		[query,
			siteFilter,
			sortOrder]);

	// Load search history
	useEffect(() => {
		try {
			const savedHistory = localStorage.getItem('gameSearchHistory');
			if (savedHistory) {
				const parsedHistory = JSON.parse(savedHistory);
				setSearchHistory(Array.isArray(parsedHistory) ? parsedHistory: []);
			}
		} catch (error) {
			console.error('Error loading search history:', error);
			setSearchHistory([]);
		}
	},
		[]);

	// Fetch recent uploads on load
	useEffect(() => {
		fetchRecentUploads();
	},
		[]);

	// Update title + favicon
	useEffect(() => {
		let newTitle = 'GameSearch';
		if (hasSearched && query) {
			newTitle = `GameSearch: ${query} results`;
		} else if (hasSearched && results.length > 0) {
			newTitle = `GameSearch: Search Results`;
		} else if (!hasSearched && recentUploads.length > 0) {
			newTitle = `GameSearch: Recent Uploads`;
		}
		document.title = newTitle;

		const link = document.createElement('link');
		link.rel = 'icon';
		link.href =
		'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>🎮</text></svg>';

		const existingLink = document.querySelector("link[rel~='icon']");
		if (existingLink) {
			existingLink.remove();
		}
		document.head.appendChild(link);

		return () => {
			document.title = 'GameSearch';
			link.remove();
		};
	},
		[query,
			hasSearched,
			results.length,
			recentUploads.length]);

	useEffect(() => {
		if (hasSearched) {
			searchGames();
		}
	},
		[sortOrder]);

	const saveToHistory = (searchTerm) => {
		const newHistory = [searchTerm, ...searchHistory.filter((h) => h !== searchTerm)].slice(0,
			10);
		setSearchHistory(newHistory);
		try {
			localStorage.setItem('gameSearchHistory',
				JSON.stringify(newHistory));
		} catch (error) {
			console.error('Error saving search history:',
				error);
		}
	};

	const fetchRecentUploads = async () => {
		setLoadingRecent(true);
		setHasSearched(false);
		setResults([]);
		setError('');
		setQuery('');
		setStats({});
		setRefineQuery('');
		try {
			const response = await fetch(`${WORKER_URL}/recent`);
			const data = await response.json();
			if (data.success) {
				setRecentUploads(data.results || []);
			} else {
				console.error('Failed to fetch recent uploads:', data.error);
				setError(data.error || 'Failed to fetch recent uploads');
			}
		} catch (err) {
			console.error('Recent uploads error:', err);
			setError('Failed to connect to API');
		} finally {
			setLoadingRecent(false);
		}
	};

	const searchGames = async (searchQuery = query) => {
		if (!searchQuery.trim()) return;
		setLoading(true);
		setHasSearched(true);
		setError('');
		setRecentUploads([]);
		try {
			const params = new URLSearchParams( {
				search: searchQuery.trim(),
				site: siteFilter,
				sort: sortOrder, // Add this line to include sort order
			});
			const response = await fetch(`${WORKER_URL}?${params}`);
			const data = await response.json();
			if (data.success) {
				setResults(data.results || []);
				setStats(data.siteStats || {});
				saveToHistory(searchQuery.trim());
			} else {
				setError(data.error || 'Search failed');
			}
		} catch (err) {
			setError('Failed to connect to search API');
			console.error('Search error:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = (e) => {
		if (e) e.preventDefault();
		searchGames();
	};

	const handleHistoryClick = (historyItem) => {
		setQuery(historyItem);
		searchGames(historyItem);
	};

	const clearSearch = () => {
		setQuery('');
		setRefineQuery('');
		setResults([]);
		setStats({});
		setError('');
		setHasSearched(false);
		fetchRecentUploads();
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleDateString();
		} catch {
			return 'Invalid Date';
		}
	};

	const getProxiedImageUrl = (originalUrl) => {
		if (!originalUrl || !originalUrl.startsWith('http')) {
			return null;
		}
		return `${WORKER_URL}/proxy-image?url=${encodeURIComponent(originalUrl)}`;
	};

	const extractGamePoster = (game) => {
		if (game.image && game.image.startsWith('http')) {
			const proxiedUrl = getProxiedImageUrl(game.image);
			if (proxiedUrl) {
				return {
					url: proxiedUrl,
					isProxied: true,
					originalUrl: game.image
				};
			}
		}
		if (game.description) {
			const posterMatch = game.description.match(
				/https?:\/\/[^\s"'<>]+\.(?:jpg|jpeg|png|gif|webp|bmp)/i
			);
			if (posterMatch) {
				const proxiedUrl = getProxiedImageUrl(posterMatch[0]);
				if (proxiedUrl) {
					return {
						url: proxiedUrl,
						isProxied: true,
						originalUrl: posterMatch[0]
					};
				}
			}
		}
		const colors = [
			'from-purple-600 to-blue-600',
			'from-blue-600 to-cyan-600',
			'from-cyan-600 to-teal-600',
			'from-teal-600 to-green-600',
			'from-green-600 to-yellow-600',
			'from-yellow-600 to-orange-600',
			'from-orange-600 to-red-600',
			'from-red-600 to-pink-600',
			'from-pink-600 to-purple-600',
		];
		const colorIndex = game.title?.charCodeAt(0) % colors.length || 0;
		return {
			url: colors[colorIndex],
			isProxied: false,
			originalUrl: null
		};
	};

	const getServiceIcon = (service) => {
		if (!service) return '💾';
		const serviceLower = service.toLowerCase();
		if (serviceLower.includes('magnet') || serviceLower.includes('torrent')) return '🧲';
		if (serviceLower.includes('mega')) return '🔴';
		if (serviceLower.includes('mediafire')) return '🟦';
		if (serviceLower.includes('google')) return '🔗';
		return '💾';
	};

	const handleImageError = (gameId, imageUrl, originalUrl) => {
		console.log(`Image failed to load for ${gameId}: ${imageUrl} (original: ${originalUrl})`);
		setFailedImages((prev) => new Set([...prev, gameId]));
	};

	// handle crypt link clicks & caching
	const handleCryptClick = async (e, cryptUrl) => {
		e.preventDefault();

		// Check if it's a filecrypt.co link
		if (cryptUrl.includes('filecrypt.co')) {
			// For FileCrypt links, just open the page in a new tab
			window.open(cryptUrl, '_blank');
			return;
		}

		// Original crypt.cybar.xyz handling
		const hash = cryptUrl.split('#')[1];
		if (!hash) {
			console.error('No hash found in crypt URL:', cryptUrl);
			alert('Invalid crypt link format');
			return;
		}

		// Check if we already have this link decrypted
		if (decryptedLinks[hash]) {
			console.log('Using cached decrypted link for hash:', hash);
			const url = decryptedLinks[hash].resolvedUrl || decryptedLinks[hash].url;
			window.open(url, '_blank');
			return;
		}

		try {
			console.log('Decrypting hash:', hash);
			const url = `${WORKER_URL}/decrypt?hash=${encodeURIComponent(hash)}`;
			console.log('Request URL:', url);

			const resp = await fetch(url);
			console.log('Response status:', resp.status);

			if (!resp.ok) {
				const errorText = await resp.text();
				console.error('Decrypt request failed:', errorText);
				alert(`Decryption failed: ${resp.status} ${resp.statusText}`);
				return;
			}

			const data = await resp.json();
			console.log('Decrypt response:', data);

			if (data.success) {
				// Get the URL from either resolvedUrl or url field
				const resolvedUrl = data.resolvedUrl || data.url;
				const service = data.service || 'Unknown';

				if (!resolvedUrl) {
					console.error('No URL found in response:', data);
					alert('No URL found in decryption response');
					return;
				}

				// Cache the decrypted link with both fields for compatibility
				setDecryptedLinks((prev) => ({
					...prev,
					[hash]: {
						resolvedUrl: resolvedUrl,
						url: resolvedUrl, // Include both for compatibility
						service: service,
						timestamp: Date.now()
					}
				}));

				console.log('Opening resolved URL:', resolvedUrl);
				window.open(resolvedUrl, '_blank');
			} else {
				console.error('Decryption failed:', data.error);
				alert(data.error || 'Failed to decrypt link');
			}
		} catch (err) {
			console.error('Decrypt error:', err);
			alert(`Error decrypting link: ${err.message}`);
		}
	};

	const GameCard = ({
		game
	}) => {
		const [showAllLinks,
			setShowAllLinks] = useState(false);

		const posterData = extractGamePoster(game);
		const {
			url: posterSrc,
			originalUrl
		} = posterData;
		const isImagePoster = posterSrc && posterSrc.startsWith('http');
		const imageHasFailed = failedImages.has(game.id);
		const shouldShowImage = isImagePoster && !imageHasFailed;

		const linksToShow = showAllLinks ? game.downloadLinks: game.downloadLinks.slice(0, 10);

		return (
			<div className="group block">
				<div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-3xl border border-gray-700/50 overflow-hidden hover:border-cyan-400/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-cyan-500/20 hover:from-gray-700/80 hover:to-gray-800/80">
					<div className="relative h-64 overflow-hidden">
						{shouldShowImage ? (
							<>
								<img
								src={posterSrc}
								alt={game.title}
								className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
								onError={() => handleImageError(game.id, posterSrc, originalUrl)}
								referrerPolicy="no-referrer"
								loading="lazy"
								/>
							<div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center opacity-0 group-hover:opacity-10 transition-opacity">
								<div className="text-4xl opacity-40">
									🎮
								</div>
							</div>
						</>
					): (
						<div
							className={`flex w-full h-full bg-gradient-to-br ${
							isImagePoster ? 'from-gray-700 to-gray-800': posterSrc
							} items-center justify-center`}
							>
							<div className="text-6xl opacity-40">
								🎮
							</div>
						</div>
					)}
					<div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent"></div>
					<div className="absolute top-4 right-4">
						<span
							className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-md ${
							game.source === 'SkidrowReloaded'
							? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg shadow-red-500/30': game.source === 'FreeGOGPCGames'
							? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/30': 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30'
							}`}
							>
							{game.source}
						</span>
					</div>
					{game.downloadLinks && game.downloadLinks.length > 0 && (
						<div className="absolute top-4 left-4">
							<span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-cyan-500 text-white backdrop-blur-md shadow-lg shadow-blue-500/30">
								{game.downloadLinks.length} Links
							</span>
						</div>
					)}
				</div>
				<div className="p-6 space-y-4">
					<h3 className="font-bold text-xl text-white leading-tight group-hover:text-cyan-400 transition-colors">
						{game.title}
					</h3>
					<p className="text-gray-300 text-sm leading-relaxed line-clamp-3">
						{game.description}
					</p>
					<div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-700/50">
						<span className="flex items-center gap-1">📅 {formatDate(game.date)}</span>
						<a
							href={game.link}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 transition-colors"
							>
							<ExternalLink className="w-3 h-3" />
							<span>View Source</span>
						</a>
					</div>
				</div>
				{game.downloadLinks && game.downloadLinks.length > 0 && (
					<div
						className="border-t border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-900/50"
						>
						<div className="p-6 space-y-3">
							<h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
								<Download className="w-4 h-4 text-cyan-400" /> Download Options
							</h4>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
								{linksToShow.map((link, index) => {
									const isCrypt = link.type === 'crypt';
									const isFileCrypt = link.type === 'filecrypt';
									const hash = isCrypt ? link.url.split('#')[1]: null;
									const filecryptId = isFileCrypt ? link.id: null;
									const resolved = hash ? decryptedLinks[hash]: (filecryptId ? decryptedLinks[filecryptId]: null);

									return (
										<div
											key={index}
											onClick={(e) => {
												if (isCrypt || isFileCrypt) {
													e.stopPropagation(); // Prevent card click when clicking download links
													if (isCrypt) {
														handleCryptClick(e, link.url);
													} else if (isFileCrypt) {
														// For FileCrypt, just open the page in a new tab
														window.open(link.url, '_blank');
													}
												}
											}}
											className={`group/link flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700/30 transition-colors bg-white/5 backdrop-blur-sm border border-white/10 hover:border-cyan-400/50 ${
											isFileCrypt && link.requiresCaptcha ? 'opacity-75': ''
											}`}
											>
											<span className="text-lg flex-shrink-0">
												{isCrypt || isFileCrypt
												? resolved
												? getServiceIcon(resolved.service): isFileCrypt
												? '🔒': '🔒': getServiceIcon(link.service)}
											</span>
											<span className="truncate flex-1 font-medium text-white group-hover/link:text-cyan-400 transition-colors">
												{isCrypt || isFileCrypt
												? resolved
												? `${resolved.service} Link`: isFileCrypt
												? link.requiresCaptcha
												? 'FileCrypt (CAPTCHA Required)': 'FileCrypt Link': 'Decrypt Link': link.text || link.service}
											</span>
											{isFileCrypt && link.requiresCaptcha && (
												<span className="text-xs bg-yellow-500 text-yellow-900 px-2 py-1 rounded-full">
													CAPTCHA
												</span>
											)}
											{!(isCrypt || isFileCrypt) && (
												<a
													href={link.url}
													target="_blank"
													rel="noopener noreferrer"
													className="ml-2 opacity-0 group-hover/link:opacity-100 transition-opacity text-cyan-400"
													onClick={(e) => e.stopPropagation()}
													>
													<ExternalLink className="w-4 h-4" />
												</a>
											)}
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													if (isCrypt || isFileCrypt) {
														if (resolved) {
															const urlToCopy = resolved.resolvedUrl || resolved.url;
															navigator.clipboard.writeText(urlToCopy);
														} else {
															navigator.clipboard.writeText(link.url);
														}
													} else {
														navigator.clipboard.writeText(link.url);
													}
												}}
												className="ml-2 opacity-0 group-hover/link:opacity-100 transition-opacity text-cyan-400 hover:text-cyan-300"
												>
												<Copy className="w-4 h-4" />
											</button>
										</div>
									);
								})}
							</div>
							{game.downloadLinks.length > 10 && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										setShowAllLinks(!showAllLinks);
									}}
									className="w-full text-xs text-cyan-400 hover:text-cyan-300 text-center py-2 mt-2 bg-gray-700/30 rounded-lg border border-gray-600/30 transition"
									>
									{showAllLinks
									? 'Show less': `+${game.downloadLinks.length - 10} more download options`}
								</button>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

// For search results (includes refine query)
const filteredSearchResults = React.useMemo(() => {
	let currentResults = results;

	if (siteFilter !== 'all') {
		const sourceMap = {
			skidrow: 'SkidrowReloaded',
			freegog: 'FreeGOGPCGames',
			gamedrive: 'GameDrive',
			steamrip: 'SteamRip',
		};
		currentResults = currentResults.filter((game) => game.source === sourceMap[siteFilter]);
	}

	// Apply refine query only to search results
	if (refineQuery.trim()) {
		const lowerCaseRefineQuery = refineQuery.trim().toLowerCase();
		currentResults = currentResults.filter(
			(game) =>
			game.title?.toLowerCase().includes(lowerCaseRefineQuery) ||
			game.description?.toLowerCase().includes(lowerCaseRefineQuery) ||
			game.slug?.toLowerCase().includes(lowerCaseRefineQuery) ||
			game.excerpt?.toLowerCase().includes(lowerCaseRefineQuery)
		);
	}

	// Sorting
	if (sortOrder === 'date') {
		currentResults = [...currentResults].sort((a, b) => new Date(b.date) - new Date(a.date));
	} else if (sortOrder === 'title') {
		currentResults = [...currentResults].sort((a, b) => a.title.localeCompare(b.title));
	} else if (sortOrder === 'size') {
		currentResults = [...currentResults].sort((a, b) => (b.size || 0) - (a.size || 0));
	}
	// relevance = default

	return currentResults;
},
	[results,
		siteFilter,
		refineQuery,
		sortOrder]);

// For recent uploads (no refine query)
const filteredRecentUploads = React.useMemo(() => {
	let currentResults = recentUploads;

	if (siteFilter !== 'all') {
		const sourceMap = {
			skidrow: 'SkidrowReloaded',
			freegog: 'FreeGOGPCGames',
			gamedrive: 'GameDrive',
			steamrip: 'SteamRip',
		};
		currentResults = currentResults.filter((game) => game.source === sourceMap[siteFilter]);
	}

	// Sorting (no refine query for recent uploads)
	if (sortOrder === 'date') {
		currentResults = [...currentResults].sort((a, b) => new Date(b.date) - new Date(a.date));
	} else if (sortOrder === 'title') {
		currentResults = [...currentResults].sort((a, b) => a.title.localeCompare(b.title));
	} else if (sortOrder === 'size') {
		currentResults = [...currentResults].sort((a, b) => (b.size || 0) - (a.size || 0));
	}
	// relevance = default

	return currentResults;
},
	[recentUploads,
		siteFilter,
		sortOrder]);

return (
	<div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
		{/* 🔴 Adblocker Notice */}
		{!hideAdblockNotice && (
			<div className="sticky top-0 z-50 bg-red-600 text-white text-sm py-2 px-4 flex items-center justify-between">
				<span>
					⚠️ For safety, always use a good adblocker like <strong>uBlock Origin</strong> when browsing external links.
				</span>
				<button
					onClick={() => {
						setHideAdblockNotice(true);
						setCookie('hideAdblockNotice', 'true', 30); // Changed from adblockNoticeClosed to hideAdblockNotice
					}}
					className="ml-4 text-white hover:text-gray-200"
					>
					<X className="w-4 h-4" />
				</button>
			</div>
		)}

		{/* 📝 FileCrypt CAPTCHA Notice */}
		{siteFilter === 'steamrip' && (
			<div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6">
				<div className="flex items-center gap-2 text-yellow-300">
					<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
						<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
					</svg>
					<span>
						<strong>Note:</strong> Some links from SteamRip are protected by FileCrypt and require solving a CAPTCHA manually.
						Clicking these links will open the FileCrypt page in a new tab where you can solve the CAPTCHA and get the download links.
					</span>
				</div>
			</div>
		)}

		<div className="container mx-auto px-4 py-8">
			{/* Header */}
			<div className="text-center mb-8">
				<h1 className="text-4xl font-bold text-white mb-2">Game Search</h1>
				<p className="text-blue-200">
					Search across multiple game sources
				</p>
			</div>
			<div className="backdrop-blur-md">
				<div className="max-w-4xl mx-auto px-4 py-4">
					<form onSubmit={handleSubmit} className="space-y-4">

						{/* Search Input + Sort Dropdown */}
						<div className="relative flex items-center">
							<Search className="absolute left-4 h-5 w-5 text-gray-400" />
							<input
							ref={searchInputRef}
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
							placeholder="Search for games..."
							className="w-full pl-12 pr-36 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>

						{/* Right side controls (clear + sort) */}
						<div className="absolute right-3 flex items-center gap-2">
							{hasSearched && (
								<button
									type="button"
									onClick={clearSearch}
									className="h-5 w-5 text-gray-400 hover:text-white transition-colors"
									>
									<X />
								</button>
							)}
							<select
								value={sortOrder}
								onChange={(e) => setSortOrder(e.target.value)}
								className="px-2 py-1 bg-white/10 text-sm text-white rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
								<option value="relevance">Relevance</option>
								<option value="date">Date</option>
								<option value="title">Title</option>
								<option value="size">File Size</option>
							</select>
						</div>
					</div>
					{/* Refine Filter */}
					{hasSearched && (
						<div className="relative flex items-center">
							<Filter className="absolute left-4 h-5 w-5 text-gray-400" />
							<input
							type="text"
							value={refineQuery}
							onChange={(e) => setRefineQuery(e.target.value)}
							placeholder="Refine results..."
							className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						{refineQuery && (
							<button
								type="button"
								onClick={() => setRefineQuery('')}
								className="absolute right-3 h-5 w-5 text-gray-400 hover:text-white transition-colors"
								>
								<X />
							</button>
						)}
					</div>
				)}

				{/* Search button */}
				<div className="text-center">
					<button
						type="submit"
						disabled={loading || !query.trim()}
						className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-xl font-semibold transition-colors inline-flex items-center gap-2"
						>
						{loading ? <Loader className="w-5 h-5 animate-spin" />: <Search className="w-5 h-5" />}
						{loading ? 'Searching...': 'Search Games'}
					</button>
				</div>
			</form>
		</div>
	</div>

	{/* Site Filter */}
	<div className="flex flex-wrap gap-3 justify-center items-center mt-6">
		{[{
			value: 'all',
			label: 'All Sites',
			color: 'from-purple-500 to-pink-500'
		},
			{
				value: 'skidrow',
				label: 'SkidRow',
				color: 'from-red-500 to-orange-500'
			},
			{
				value: 'freegog',
				label: 'FreeGOG',
				color: 'from-green-500 to-emerald-500'
			},
			{
				value: 'gamedrive',
				label: 'GameDrive',
				color: 'from-blue-500 to-cyan-500'
			},
			{
				value: 'steamrip',
				label: 'SteamRip',
				color: 'from-yellow-500 to-amber-500'
			},
		].map(option => (
				<button
					key={option.value}
					type="button"
					onClick={() => setSiteFilter(option.value)}
					className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
					siteFilter === option.value
					? `bg-gradient-to-r ${option.color} text-white shadow-lg shadow-${option.color.split('-')[1]}-500/25`: 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/20'
					}`}
					>
					<Filter className="inline w-4 h-4 mr-2" />
					{option.label}
				</button>
			))}
	</div>

	{/* Search History */}
	{searchHistory.length > 0 && (
		<div className="mt-4">
			<p className="text-sm text-gray-300 mb-2">
				Recent searches:
			</p>
			<div className="flex flex-wrap gap-2">
				{searchHistory.map((item, index) => (
					<button
						key={index}
						onClick={() => handleHistoryClick(item)}
						className="px-3 py-1 bg-white/10 hover:bg-white/20 text-gray-300 rounded-full text-sm transition-colors"
						>
						{item}
					</button>
				))}
			</div>
		</div>
	)}
</div>

{
/* Stats (only show when there are search results) */
}
{
hasSearched && Object.keys(stats).length > 0 && (
	<div className="max-w-4xl mx-auto mb-6">
		<div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
			<div className="flex flex-wrap gap-4 justify-center text-sm text-gray-300">
				{Object.entries(stats).map(([site, count]) => (
					<span key={site} className="flex items-center gap-1">
						<span className="w-2 h-2 bg-blue-400 rounded-full"></span>
						{site}: {count} results
					</span>
				))}
				<span className="flex items-center gap-1 font-semibold">
					<span className="w-2 h-2 bg-green-400 rounded-full"></span>
					Total: {results.length}
				</span>
			</div>
		</div>
	</div>
)}

{
/* Error Message */
}
{
error && (
	<div className="max-w-4xl mx-auto mb-6">
		<div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-200 text-center">
			<X className="inline w-5 h-5 mr-2" />
			{error}
		</div>
	</div>
)}

{
/* Recent Uploads Section - Show when no search has been made */
}
{
!hasSearched && recentUploads.length > 0 && (
	<div className="max-w-7xl mx-auto">
		<div className="mb-8">
			<div className="bg-gradient-to-r from-purple-600/20 to-orange-600/20 backdrop-blur-md rounded-2xl border border-purple-500/30 p-6">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
							<Clock className="w-7 h-7 text-orange-400" />
							Recent Uploads
						</h2>
						<p className="text-purple-200">
							Latest games uploaded across all sources
						</p>
					</div>
					<div className="text-right">
						<button
							onClick={fetchRecentUploads}
							disabled={loadingRecent}
							className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-400/50 rounded-lg text-purple-300 hover:text-white transition-all duration-200 text-sm font-medium flex items-center gap-2"
							>
							{loadingRecent ? (
								<Loader className="w-4 h-4 animate-spin" />
							): (
								<Clock className="w-4 h-4" />
							)}
							{loadingRecent ? 'Loading...': 'Refresh'}
						</button>
					</div>
				</div>
			</div>
		</div>

		{loadingRecent ? (
			<div className="text-center py-12">
				<Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-purple-400" />
				<p className="text-gray-400">
					Loading recent uploads...
				</p>
			</div>
		): filteredRecentUploads.length > 0 ? (
			<>
				<div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
					{filteredRecentUploads.map((game) => (
						<GameCard key={game.id} game={game} />
					))}
				</div>
				<div className="text-center text-gray-400 text-sm mt-4">
					Showing {filteredRecentUploads.length} recent uploads
				</div>
			</>
		): (
			<div className="text-center text-gray-400 py-12">
				<Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
				<p>
					{siteFilter === 'all'
					? 'No recent uploads available': `No recent uploads available for ${siteFilter === 'skidrow' ? 'Skidrow': siteFilter === 'freegog' ? 'FreeGOG': siteFilter === 'gamedrive' ? 'GameDrive': 'SteamRip'}`}
				</p>
				{siteFilter !== 'all' && (
					<button
						onClick={() => setSiteFilter('all')}
						className="mt-4 px-6 py-2 bg-white/10 hover:bg-white/20 text-gray-300 rounded-lg transition-colors"
						>
						Show all sites
					</button>
				)}
			</div>
		)}
	</div>
)}

{
/* Search Results - Show when search has been made */
}
{
hasSearched && filteredSearchResults.length > 0 && (
	<div className="max-w-7xl mx-auto">
		<div className="mb-8">
			<div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-md rounded-2xl border border-blue-500/30 p-6">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-bold text-white mb-2">Search Results</h2>
						<p className="text-blue-200">
							Found {filteredSearchResults.length} games
						</p>
					</div>
					<div className="text-right">
						<div className="flex flex-wrap gap-3 justify-end">
							{Object.entries(stats).map(([site, count]) => (
								<span
									key={site}
									className={`px-3 py-1 rounded-full text-xs font-bold ${
									site === 'SkidrowReloaded'
									? 'bg-red-500/20 text-red-300 border border-red-400/50': site === 'FreeGOGPCGames'
									? 'bg-green-500/20 text-green-300 border border-green-400/50': site === 'GameDrive'
									? 'bg-purple-500/20 text-purple-300 border border-purple-400/50': site === 'SteamRip'
									? 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/50': 'bg-gray-500/20 text-gray-300 border border-gray-400/50'
									}`}
									>
									{site === 'SkidrowReloaded'
									? 'Skidrow': site === 'FreeGOGPCGames'
									? 'FreeGOG': site === 'GameDrive'
									? 'GameDrive': site === 'SteamRip'
									? 'SteamRip': site}
									: {count}
								</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>

		<div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
			{filteredSearchResults.map((game) => (
				<GameCard key={game.id} game={game} />
			))}
		</div>
		<div className="text-center text-gray-400 text-sm mt-4">
			Showing {filteredSearchResults.length} search results
		</div>
	</div>
)}

{
/* Empty State for Search Results */
}
{
hasSearched && filteredSearchResults.length === 0 && !loading && !error && (
	<div className="text-center text-gray-400 py-12">
		<Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
		<p>
			No games found for your search
		</p>
		<button
			onClick={clearSearch}
			className="mt-4 px-6 py-2 bg-white/10 hover:bg-white/20 text-gray-300 rounded-lg transition-colors"
			>
			View Recent Uploads
		</button>
	</div>
)}

{
/* Empty State for Initial Load */
}
{
!hasSearched && recentUploads.length === 0 && !loadingRecent && (
	<div className="text-center text-gray-400 py-12">
		<Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
		<p>
			Search for games or check out recent uploads
		</p>
	</div>
)}

{
/* 🔗 Other Projects Section */
} < div className = "max-w-4xl mx-auto mt-16" >
<h2 className="text-2xl font-bold text-white mb-6 text-center">
	Other Projects
</h2>
<div className="grid gap-6 sm:grid-cols-2">
	<a
		href="https://pdbypass.a7a8524.workers.dev/"
		target="_blank"
		rel="noopener noreferrer"
		className="p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition"
		>
		<h3 className="text-lg font-semibold text-cyan-400 mb-2">
			Pixeldrain Bypass
		</h3>
		<p className="text-sm text-gray-300">
			Remove Pixeldrain download limits with a Cloudflare Worker proxy.
		</p>
	</a>

	<a
		href="https://cfrss.a7a8524.workers.dev/"
		target="_blank"
		rel="noopener noreferrer"
		className="p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition"
		>
		<h3 className="text-lg font-semibold text-cyan-400 mb-2">
			RSS Cloudflare Bypass
		</h3>
		<p className="text-sm text-gray-300">
			Use FlareSolverr via a Cloudflare Worker to fetch RSS feeds behind CF protection.
		</p>
	</a>
</div>
</div>

{
/* Footer */
} < div className = "text-center mt-12 text-gray-400 text-sm" >
<p>
Powered by Cloudflare Workers • Search across multiple game sources
</p>
</div>
</div>
);
};

export default GameSearchApp;